import React from 'react';
import {
  CheckCircle, AlertCircle, Clock, XCircle, Activity, Shield,
} from 'lucide-react';

export const STATE_CONFIG = {
  'Vigente':              { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  'Vigente desde':        { color: 'bg-teal-100 text-teal-800',       icon: Clock },
  'En trámite':           { color: 'bg-sky-100 text-sky-800',         icon: Clock },
  'Expira pronto':        { color: 'bg-yellow-100 text-yellow-800',   icon: Clock },
  'Suspendida':           { color: 'bg-orange-100 text-orange-800',   icon: AlertCircle },
  'Pago pendiente':       { color: 'bg-yellow-100 text-yellow-800',   icon: Clock },
  'Pago rechazado':       { color: 'bg-red-100 text-red-800',         icon: XCircle },
  'Pago vencido':         { color: 'bg-red-100 text-red-800',         icon: XCircle },
  'Siniestro abierto':    { color: 'bg-blue-100 text-blue-800',       icon: AlertCircle },
  'Asistencia en camino': { color: 'bg-purple-100 text-purple-800',   icon: Activity },
  'Cancelada':            { color: 'bg-gray-100 text-gray-600',       icon: XCircle },
  'Renovada':             { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
};

export const INSURANCE_LABELS = {
  incendio: 'Incendio', salud: 'Salud', auto: 'Automotriz', hogar: 'Hogar',
  viaje: 'Viaje', vida: 'Vida', soap: 'SOAP', rc: 'Resp. Civil',
};

export const ramoLabel = (ramo) =>
  INSURANCE_LABELS[ramo] ?? (ramo ? ramo.charAt(0).toUpperCase() + ramo.slice(1) : '—');

export const STATUS_OPTIONS = [
  { value: 'active',        label: 'Vigente' },
  { value: 'expiring_soon', label: 'Expira pronto' },
  { value: 'suspended',     label: 'Suspendida' },
  { value: 'pending',       label: 'Pago pendiente' },
  { value: 'rejected',      label: 'Pago rechazado' },
  { value: 'canceled',      label: 'Cancelada' },
];

export const BROADCAST_EVENTS = [
  { value: 'payment.rejected',       label: 'Pago rechazado',       target: 'Pago rechazado' },
  { value: 'payment.overdue',        label: 'Pago vencido',         target: 'Pago vencido' },
  { value: 'payment.pending',        label: 'Pago pendiente',       target: 'Pago pendiente' },
  { value: 'payment.paid',           label: 'Pago confirmado',      target: 'al día (pagado)' },
  { value: 'coverage.suspended',     label: 'Cobertura suspendida', target: 'Suspendida' },
  { value: 'coverage.activated',     label: 'Cobertura activada',   target: 'Vigente' },
  { value: 'coverage.canceled',      label: 'Cobertura cancelada',  target: 'Cancelada' },
  { value: 'coverage.expiring_soon', label: 'Expira pronto',        target: 'Expira pronto' },
];

export const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const fmtDatetime = (iso) =>
  iso ? new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export const StatePill = ({ state }) => {
  const cfg = STATE_CONFIG[state] ?? { color: 'bg-gray-100 text-gray-600', icon: Shield };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {state}
    </span>
  );
};

// ─── LIQUID GLASS (traducción web del material iOS 26) ──────────────────────────
// El glass necesita contenido detrás para difuminar: usa <GlassAura/> en la página.

// Material base reutilizable: translúcido + backdrop-blur + ring + sombra suave.
export const GLASS =
  'bg-white/55 dark:bg-slate-900/45 backdrop-blur-xl ' +
  'border border-white/60 dark:border-white/10 ring-1 ring-black/[0.04] dark:ring-white/[0.06] ' +
  'shadow-[0_8px_30px_rgba(2,32,28,0.08)] rounded-2xl';

// Tarjeta de vidrio. `interactive` añade respuesta al puntero (lift + ring de marca).
export function GlassCard({ className = '', interactive = false, children, ...props }) {
  return (
    <div
      className={[
        GLASS,
        interactive
          ? 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_44px_rgba(2,32,28,0.14)] hover:ring-[#007D6E]/25 cursor-pointer'
          : '',
        className,
      ].join(' ')}
      {...props}
    >
      {children}
    </div>
  );
}

// Fondo aurora: blobs de marca difuminados que dan profundidad al vidrio.
// Renderizar como primer hijo de un contenedor `relative isolate`.
export function GlassAura({ className = '' }) {
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${className}`}>
      <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-[#007D6E]/25 blur-3xl" />
      <div className="absolute top-1/4 -right-28 h-[26rem] w-[26rem] rounded-full bg-orange-400/20 blur-3xl" />
      <div className="absolute bottom-[-6rem] left-1/3 h-[24rem] w-[24rem] rounded-full bg-sky-400/15 blur-3xl" />
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-slate-100/30 dark:from-slate-950/40 dark:to-slate-900/30" />
    </div>
  );
}

// Tile KPI de vidrio.
export function GlassKpi({ label, value, icon: Icon, tint = 'text-[#007D6E]', loading = false }) {
  return (
    <GlassCard className="px-4 py-3.5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold leading-tight mt-0.5">{loading ? '—' : value}</p>
        </div>
        {Icon && (
          <span className={`grid place-items-center h-10 w-10 rounded-xl bg-white/50 dark:bg-white/5 ring-1 ring-black/5 dark:ring-white/10 ${tint}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
      </div>
    </GlassCard>
  );
}
